// "Connect to Mission Control", the app's side (card connuv2g86pr stage 2; the protocol is
// docs/plan/phase7/CONNECT-PROTOCOL.md in the Mission Control repo).
//
// Two things live here, both shop-admin only:
//
//   McConnect  the Connect page. Mission Control sends the person here with a workspace id and a return
//              address and nothing secret; they sign in to this app as they always do, see what is about
//              to be linked, and confirm once. The server makes the one-time code and says where to send
//              them back to; the browser only follows that.
//   McPanel    the "Linked to Mission Control" panel, with Unlink, so the link can always be ended from
//              this side as well.
//
// Nothing here stores or handles a Mission Control password or token, and the shared secret that
// authenticates the machine-to-machine side never comes near the browser.
import React, { useState, useEffect, useCallback } from 'react';
import { T, font } from '../utils/theme';
import * as api from '../utils/api';
import { Btn, Card } from './UI';

// What Mission Control put in the URL when it sent the person here. Read from the real location rather
// than kept in state, because signing in re-renders the whole admin panel and this has to survive it.
//
// The workspace *name* is the one thing here that cannot be checked: Mission Control has not signed this
// URL, so anyone able to put a link in front of a shop admin can choose what name it claims. So the page
// shows the id, which is what the code is actually bound to and what Mission Control redeems against, and
// presents the name as what the link says rather than as fact. See docs/MISSION-CONTROL-LINK.md.
export function connectRequest() {
  try {
    const p = new URLSearchParams(window.location.search);
    if (!p.get('mc_connect')) return null;
    const workspaceId = (p.get('workspace_id') || '').trim();
    if (!/^[A-Za-z0-9_-]{1,64}$/.test(workspaceId)) return null;
    return {
      workspaceId,
      workspaceName: (p.get('workspace_name') || '').trim().slice(0, 80),
      returnUrl: (p.get('return_url') || p.get('return') || '').trim(),
    };
  } catch (e) { return null; }
}

// Takes the Connect parameters off the address bar once the link is made, so a reload does not show the
// page again and the code is not left sitting in history.
function clearConnectParams() {
  try {
    const url = new URL(window.location.href);
    for (const k of ['mc_connect', 'workspace_id', 'workspace_name', 'return_url', 'return']) url.searchParams.delete(k);
    window.history.replaceState({}, '', url.pathname + (url.searchParams.toString() ? '?' + url.searchParams : '') + url.hash);
  } catch (e) { /* nothing worth breaking the page over */ }
}

const Z = (lang, en, zh) => (lang === 'zh' ? zh : en);

// A line of "label: value", the shape the rest of this app uses for a read-only fact.
function Fact({ label, children, mono }) {
  return (
    <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap', marginTop: 6 }}>
      <span style={{ fontSize: 11, fontWeight: 700, color: T.dim, textTransform: 'uppercase', letterSpacing: '0.04em', minWidth: 120 }}>{label}</span>
      <span style={{ fontSize: 13, color: T.text, fontWeight: 600, fontFamily: mono ? 'ui-monospace, SFMono-Regular, Menlo, monospace' : font, wordBreak: mono ? 'break-all' : 'normal', overflowWrap: 'anywhere' }}>{children}</span>
    </div>
  );
}

export function McConnect({ lang = 'en', request, onDone }) {
  const [link, setLink] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.getMcLink().then(l => { if (!cancelled) setLink(l); }).catch(e => { if (!cancelled) setErr(e.message); });
    return () => { cancelled = true; };
  }, []);

  const confirm = async () => {
    setBusy(true); setErr('');
    try {
      const { redirect_to } = await api.createMcLinkCode(request.workspaceId, request.returnUrl);
      clearConnectParams();
      // The server decided this address and checked it is Mission Control's; the browser only follows it.
      window.location.href = redirect_to;
    } catch (e) {
      setErr(e.message);
      setBusy(false);
    }
  };

  const cancel = () => { clearConnectParams(); onDone && onDone(); };

  const wrap = { minHeight: '100vh', background: T.bg, fontFamily: font, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 };
  const inner = { width: '100%', maxWidth: 560 };

  if (err && !link) {
    return (
      <div style={wrap}><div style={inner}><Card>
        <div style={{ fontSize: 16, fontWeight: 800, color: T.danger }}>{Z(lang, 'Could not read this shop', '无法读取该店铺')}</div>
        <div style={{ fontSize: 13, color: T.muted, marginTop: 8 }}>{err}</div>
        <div style={{ marginTop: 16 }}><Btn variant="secondary" onClick={cancel}>{Z(lang, 'Back to the admin panel', '返回管理面板')}</Btn></div>
      </Card></div></div>
    );
  }
  if (!link) return <div style={wrap}><div style={{ color: T.muted, fontFamily: font, fontSize: 13 }}>{Z(lang, 'Loading…', '加载中…')}</div></div>;

  // Linking cannot work until the owner has put the shared secret and the Mission Control address in
  // backend/.env. Say which, rather than failing in a way that looks like a fault in the page.
  if (!link.configured) {
    return (
      <div style={wrap}><div style={inner}><Card>
        <div style={{ fontSize: 16, fontWeight: 800, color: T.text }}>{Z(lang, 'Mission Control linking is not set up on this server', '此服务器尚未设置 Mission Control 连接')}</div>
        <div style={{ fontSize: 13, color: T.muted, marginTop: 10, lineHeight: 1.6 }}>{Z(lang, 'Ask whoever runs this server to set it up. Until then nothing can be linked.', '请联系服务器管理员进行设置。在此之前无法连接。')}</div>
        {link.setup_hint && <div style={{ fontSize: 12, color: T.dim, marginTop: 10, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', background: T.surfaceAlt, border: `1px solid ${T.border}`, borderRadius: 8, padding: '10px 12px' }}>{link.setup_hint}</div>}
        <div style={{ marginTop: 16 }}><Btn variant="secondary" onClick={cancel}>{Z(lang, 'Back to the admin panel', '返回管理面板')}</Btn></div>
      </Card></div></div>
    );
  }

  const movingFrom = link.linked && link.workspace_id !== request.workspaceId ? link.workspace_id : null;

  return (
    <div style={wrap}>
      <div style={inner}>
        <Card>
          <div style={{ fontSize: 11, fontWeight: 800, color: T.accent, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
            {Z(lang, 'Mission Control', 'Mission Control')}
          </div>
          <div style={{ fontSize: 19, fontWeight: 900, color: T.text, marginTop: 8, lineHeight: 1.35 }}>
            {Z(lang, `Link ${link.shop_name} to the Mission Control workspace ${request.workspaceName || request.workspaceId}?`,
                     `将 ${link.shop_name} 连接到 Mission Control 工作区 ${request.workspaceName || request.workspaceId}？`)}
          </div>

          <div style={{ marginTop: 16, paddingTop: 14, borderTop: `1px solid ${T.border}` }}>
            <Fact label={Z(lang, 'This shop', '本店铺')}>{link.shop_name} <span style={{ color: T.dim, fontWeight: 500 }}>({link.shop_id})</span></Fact>
            {/* The id is the fact. The name is only what the link claims, and is labelled that way. */}
            <Fact label={Z(lang, 'Workspace id', '工作区 ID')} mono>{request.workspaceId}</Fact>
            {request.workspaceName && (
              <>
                <Fact label={Z(lang, 'Called', '名称')}>{request.workspaceName}</Fact>
                <div style={{ fontSize: 11.5, color: T.dim, marginTop: 3, marginLeft: 130, lineHeight: 1.5 }}>
                  {Z(lang, 'what the link says; only the id above is checked', '链接声称的名称；仅上方 ID 经过验证')}
                </div>
              </>
            )}
            <Fact label={Z(lang, 'Mission Control', 'Mission Control')} mono>{link.mission_control}</Fact>
          </div>

          <div style={{ marginTop: 16, background: T.surfaceAlt, border: `1px solid ${T.border}`, borderRadius: 10, padding: '12px 14px' }}>
            <div style={{ fontSize: 12, fontWeight: 800, color: T.text }}>{Z(lang, 'What this gives Mission Control', '这将授予 Mission Control 的权限')}</div>
            <ul style={{ margin: '8px 0 0', paddingLeft: 18, fontSize: 12, color: T.muted, lineHeight: 1.7 }}>
              <li>{Z(lang, `Read-only access to ${link.shop_name}’s orders and shipping costs. It can never write anything here.`,
                           `只读访问 ${link.shop_name} 的订单与运费。它永远无法在此写入任何内容。`)}</li>
              <li>{Z(lang, 'Nothing about any other shop on this server.', '不涉及本服务器上任何其他店铺的数据。')}</li>
              <li>{Z(lang, 'No password of yours. Mission Control never sees how you signed in here.', '不涉及您的任何密码。Mission Control 永远不会看到您在此的登录方式。')}</li>
              <li>{Z(lang, 'You can end it at any time, from either side.', '您可以随时从任意一方结束连接。')}</li>
            </ul>
          </div>

          {movingFrom && (
            <div style={{ marginTop: 14, background: T.warnBg, border: `1px solid ${T.warn}55`, borderRadius: 10, padding: '12px 14px', fontSize: 12.5, color: T.text, lineHeight: 1.6 }}>
              <strong>{Z(lang, 'This shop is already linked.', '该店铺已连接。')}</strong>{' '}
              {Z(lang, `It is linked to workspace ${movingFrom}. Confirming moves it to ${request.workspaceId}, and the old workspace stops seeing this shop’s figures.`,
                       `它目前连接到工作区 ${movingFrom}。确认后将改为 ${request.workspaceId}，原工作区将无法再查看本店铺的数据。`)}
            </div>
          )}

          {err && <div style={{ marginTop: 14, color: T.danger, fontSize: 13, fontWeight: 600 }}>{err}</div>}

          <div style={{ display: 'flex', gap: 10, marginTop: 18, flexWrap: 'wrap' }}>
            <Btn disabled={busy} onClick={confirm}>
              {busy ? Z(lang, 'Linking…', '连接中…') : movingFrom ? Z(lang, 'Yes, move the link', '是，转移连接') : Z(lang, 'Yes, link this shop', '是，连接本店铺')}
            </Btn>
            <Btn variant="secondary" disabled={busy} onClick={cancel}>{Z(lang, 'No, cancel', '取消')}</Btn>
          </div>
          <div style={{ fontSize: 11, color: T.dim, marginTop: 12, lineHeight: 1.6 }}>
            {Z(lang, 'Confirming sends you back to Mission Control with a one-time code that is good for ten minutes and cannot be used twice.',
                     '确认后将带着一个一次性代码返回 Mission Control；该代码十分钟内有效且只能使用一次。')}
          </div>
        </Card>
      </div>
    </div>
  );
}

// The sidebar's "Mission Control" panel, shown inside the existing modal.
export function McPanel({ lang = 'en', onClose }) {
  const [link, setLink] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const load = useCallback(async () => {
    try { setLink(await api.getMcLink()); } catch (e) { setErr(e.message); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const unlink = async () => {
    setBusy(true); setErr('');
    try { setLink(await api.unlinkMc()); setConfirming(false); } catch (e) { setErr(e.message); }
    setBusy(false);
  };

  if (!link) {
    return <div style={{ fontFamily: font, fontSize: 13, color: T.muted }}>{err || Z(lang, 'Loading…', '加载中…')}</div>;
  }

  return (
    <div style={{ fontFamily: font }}>
      <div style={{ fontSize: 16, fontWeight: 900, color: T.text }}>
        {link.linked ? Z(lang, 'Linked to Mission Control', '已连接到 Mission Control') : Z(lang, 'Mission Control', 'Mission Control')}
      </div>

      {link.linked ? (
        <>
          <div style={{ fontSize: 12.5, color: T.muted, marginTop: 8, lineHeight: 1.6 }}>
            {Z(lang, `Mission Control can read ${link.shop_name}’s orders and shipping costs. It cannot write anything here.`,
                     `Mission Control 可以读取 ${link.shop_name} 的订单与运费，但无法在此写入任何内容。`)}
          </div>
          <div style={{ marginTop: 12, paddingTop: 10, borderTop: `1px solid ${T.border}` }}>
            <Fact label={Z(lang, 'Shop', '店铺')}>{link.shop_name}</Fact>
            <Fact label={Z(lang, 'Workspace', '工作区')} mono>{link.workspace_id}</Fact>
            {link.linked_at && <Fact label={Z(lang, 'Linked', '连接于')}>{link.linked_at}</Fact>}
            {link.last_confirmed_at && <Fact label={Z(lang, 'Last confirmed', '最后确认')}>{link.last_confirmed_at}</Fact>}
          </div>
          {err && <div style={{ marginTop: 12, color: T.danger, fontSize: 13, fontWeight: 600 }}>{err}</div>}
          {confirming ? (
            <div style={{ marginTop: 16, background: T.dangerBg, border: `1px solid ${T.danger}55`, borderRadius: 10, padding: '12px 14px' }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: T.text, lineHeight: 1.6 }}>
                {Z(lang, 'Unlink this shop from Mission Control? Its figures stop showing there. Nothing in this app changes, and you can link again later.',
                         '要断开本店铺与 Mission Control 的连接吗？其数据将不再显示在那里。本应用中的内容不会改变，之后可以重新连接。')}
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
                <Btn variant="danger" disabled={busy} onClick={unlink}>{Z(lang, 'Yes, unlink', '是，断开连接')}</Btn>
                <Btn variant="secondary" disabled={busy} onClick={() => setConfirming(false)}>{Z(lang, 'Cancel', '取消')}</Btn>
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', gap: 8, marginTop: 16, flexWrap: 'wrap' }}>
              <Btn variant="danger" onClick={() => setConfirming(true)}>{Z(lang, 'Unlink', '断开连接')}</Btn>
              <Btn variant="secondary" onClick={onClose}>{Z(lang, 'Close', '关闭')}</Btn>
            </div>
          )}
        </>
      ) : (
        <>
          <div style={{ fontSize: 12.5, color: T.muted, marginTop: 8, lineHeight: 1.6 }}>
            {link.status === 'unlinked'
              ? Z(lang, `${link.shop_name} is not linked to Mission Control. ${link.reason || ''}${link.unlinked_at ? ` (${link.unlinked_at})` : ''}`,
                        `${link.shop_name} 未连接到 Mission Control。${link.reason || ''}${link.unlinked_at ? `（${link.unlinked_at}）` : ''}`)
              : Z(lang, `${link.shop_name} is not linked to Mission Control.`, `${link.shop_name} 未连接到 Mission Control。`)}
          </div>
          <div style={{ fontSize: 12.5, color: T.muted, marginTop: 10, lineHeight: 1.6 }}>
            {link.configured
              ? Z(lang, 'Linking starts in Mission Control: open its App library and click Connect, and it will send you back here to confirm.',
                        '连接从 Mission Control 开始：打开其应用库并点击"连接"，它会把您送回此处确认。')
              : Z(lang, 'Linking is not set up on this server.', '此服务器尚未设置连接。')}
          </div>
          {!link.configured && link.setup_hint && (
            <div style={{ fontSize: 12, color: T.dim, marginTop: 10, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', background: T.surfaceAlt, border: `1px solid ${T.border}`, borderRadius: 8, padding: '10px 12px' }}>{link.setup_hint}</div>
          )}
          {err && <div style={{ marginTop: 12, color: T.danger, fontSize: 13, fontWeight: 600 }}>{err}</div>}
          <div style={{ marginTop: 16 }}><Btn variant="secondary" onClick={onClose}>{Z(lang, 'Close', '关闭')}</Btn></div>
        </>
      )}
    </div>
  );
}
